import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';

import { GiftCatalogCardSkeleton } from '../../../molecules/gift-catalog-card/gift-catalog-card';

export const GiftCatalogCoreSkeleton: React.FC<{
  itemCount?: number;
}> = ({ itemCount = 9 }) => {
  const headerTitleStyle = { width: 'min(22rem, 92%)' };
  const headerSubtitleStyle = { width: 'min(36rem, 96%)' };
  const filterChipSkeletons = [
    116, 148, 134, 124, 152, 136, 120, 142, 118, 128, 144, 126,
  ].map((width, idx) => ({
    key: `gift-filter-chip-${idx + 1}`,
    width,
  }));
  const mainChipSkeletons = [86, 108, 94];
  const cardSkeletonKeys = Array.from(
    { length: itemCount },
    (_, idx) => `gift-catalog-card-skeleton-${idx + 1}`,
  );

  return (
    <section className="GiftCatalogCore component">
      <div className="GiftCatalogCore__container container grid gap-comfy-sm md:gap-comfy-xl">
        <div className="GiftCatalogCore__header flex flex-col gap-comfy-md md:items-end md:flex-row">
          <div className="flex-1">
            <div className="flex flex-col gap-snug-sm">
              <Skeleton height={42} style={headerTitleStyle} />
              <Skeleton height={20} style={headerSubtitleStyle} />
            </div>
          </div>

          <div className="flex gap-comfy-sm w-full md:w-auto">
            <div className="GiftCatalogCore__filterToggle md:hidden flex-1">
              <div className="GiftCatalogCore__filterButton flex items-center justify-between gap-snug-sm rounded-lg border border-neutral-mild px-comfy-sm py-snug-sm">
                <Skeleton height={18} width="65%" />
                <Skeleton height={18} width={20} borderRadius="9999px" />
              </div>
            </div>

            <div className="GiftCatalogCore__sort grid gap-snug-md relative flex-1 md:flex-none md:w-60">
              <div className="hidden md:block">
                <Skeleton height={16} width={90} />
              </div>
              <div className="rounded-lg border border-neutral-mild px-comfy-sm py-snug-sm">
                <Skeleton height={18} width="100%" />
              </div>
            </div>
          </div>
        </div>

        <div className="GiftCatalogCore__body grid items-start gap-comfy-lg md:grid-cols-[minmax(0,280px)_1fr]">
          <aside className="GiftCatalogCore__filters hidden md:grid px-comfy-sm py-comfy-lg rounded-lg gap-comfy-md">
            <div className="GiftCatalogCore__filtersHeader flex items-center justify-between">
              <Skeleton height={22} style={{ width: '7.5rem' }} />
              <Skeleton height={16} style={{ width: '4.25rem' }} />
            </div>

            <div className="GiftCatalogCore__filterSection grid gap-snug-md">
              <div className="GiftCatalogCore__filterButton justify-between -mx-comfy-xs mt-comfy-xs mb-comfy-xs flex items-center gap-snug-sm rounded-lg border border-neutral-mild px-comfy-sm py-snug-sm">
                <Skeleton height={18} style={{ width: '8.5rem' }} />
                <Skeleton height={18} width={20} borderRadius="9999px" />
              </div>
              <div className="GiftCatalogCore__filterBody pb-comfy-sm grid gap-snug-md">
                <div className="flex flex-wrap gap-snug-sm">
                  {filterChipSkeletons.map(({ key, width }) => (
                    <Skeleton
                      key={key}
                      height={32}
                      width={width}
                      borderRadius="9999px"
                    />
                  ))}
                </div>
              </div>
            </div>

            <hr className="GiftCatalogCore__divider block m-none" />

            <div className="GiftCatalogCore__filterSection grid gap-snug-md">
              <div className="GiftCatalogCore__filterButton justify-between -mx-comfy-xs mt-comfy-xs mb-comfy-xs flex items-center gap-snug-sm rounded-lg border border-neutral-mild px-comfy-sm py-snug-sm">
                <Skeleton height={18} style={{ width: '9rem' }} />
                <Skeleton height={18} width={20} borderRadius="9999px" />
              </div>
              <div className="GiftCatalogCore__filterBody pb-comfy-sm grid gap-snug-md">
                <Skeleton height={16} style={{ width: '7rem' }} />
                <div className="grid gap-snug-xs">
                  <Skeleton height={10} width="100%" borderRadius="9999px" />
                  <div className="flex justify-between gap-snug-lg">
                    <div className="grid gap-snug-xxs">
                      <Skeleton height={10} width={52} />
                      <Skeleton height={22} width={64} />
                    </div>
                    <div className="grid gap-snug-xxs">
                      <Skeleton height={10} width={68} />
                      <Skeleton height={22} width={64} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <div className="GiftCatalogCore__main grid gap-comfy-lg">
            <div className="grid gap-snug-sm">
              <Skeleton height={20} style={{ width: '15rem' }} />
              <div className="flex flex-wrap gap-snug-sm">
                {mainChipSkeletons.map((width, idx) => (
                  <Skeleton
                    key={`gift-main-chip-${idx + 1}`}
                    height={28}
                    width={width}
                    borderRadius="9999px"
                  />
                ))}
              </div>
            </div>

            <div className="grid gap-comfy-sm @md:gap-comfy-lg grid-cols-1 @sm:grid-cols-2 @md:grid-cols-3">
              {cardSkeletonKeys.map((key) => (
                <GiftCatalogCardSkeleton key={key} />
              ))}
            </div>

            <div className="flex justify-center mt-comfy-lg">
              <Skeleton height={48} width={200} borderRadius="9999px" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const GiftCatalogCoreCardsSkeleton: React.FC<{
  itemCount?: number;
}> = ({ itemCount = 9 }) => {
  const summaryChipWidths = [88, 110, 96];
  const cardSkeletonKeys = Array.from(
    { length: itemCount },
    (_, idx) => `gift-catalog-card-skeleton-inline-${idx + 1}`,
  );

  return (
    <>
      <div className="grid gap-snug-sm">
        <Skeleton height={20} style={{ width: '15rem' }} />
        <div className="flex flex-wrap gap-snug-sm">
          {summaryChipWidths.map((width, idx) => (
            <Skeleton
              key={`gift-summary-chip-${idx + 1}`}
              height={28}
              width={width}
              borderRadius="9999px"
            />
          ))}
        </div>
      </div>

      <div className="grid gap-comfy-sm @md:gap-comfy-lg grid-cols-1 @sm:grid-cols-2 @md:grid-cols-3">
        {cardSkeletonKeys.map((key) => (
          <GiftCatalogCardSkeleton key={key} />
        ))}
      </div>
    </>
  );
};
